/**
 * Validacion de documentos por pais (`checkDocumento`).
 * Reglas y ejemplos en este archivo; validadores en `documentoIdentidadValidators.js`.
 */
import { validateWithValidator } from './documentoIdentidadValidators.js';

/** Tabla de letras de control DNI/NIE (Espana). */
export const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';

const DOCUMENT_SCOPE = {
  PARTICULAR: 'particular',
  EMPRESA: 'empresa',
  PASSPORT: 'passport',
};

const DOCUMENT_RULES_BY_COUNTRY = {
  AT: [
    {
      type: 'PERSONALAUSWEIS',
      regex: '^[A-Z0-9]{7,9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'A1234567',
    },
    {
      type: 'STEUERNUMMER',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789',
    },
    {
      type: 'UID',
      regex: '^ATU\\d{8}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: 'ATU12345678',
    },
  ],

  BE: [
    {
      type: 'NATIONAL_NUMBER',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],

  BG: [
    {
      type: 'EGN',
      regex: '^\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890',
    },
  ],
  CH: [
    {
      type: 'ID',
      regex: '^[A-Z0-9]{8}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'A1234567',
    },
    {
      type: 'AVS',
      regex: '^756\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '7561234567890',
    },
    {
      type: 'UID',
      regex: '^CHE\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: 'CHE123456789',
    },
  ],
  CY: [
    {
      type: 'TAFTOTITA',
      regex: '^\\d{5,10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567',
    },
    {
      type: 'TIC',
      regex: '^6\\d{7}[A-Z]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '61234567A',
    },
  ],
  CZ: [
    {
      type: 'RODNÉ_ČÍSLO',
      regex: '^\\d{9,10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890',
    },
    {
      type: 'DIČ',
      regex: '^CZ\\d{8,10}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: 'CZ12345678',
    },
  ],
  DE: [
    {
      type: 'PERSONALAUSWEIS',
      regex: '^[A-Z0-9]{9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'A12345678',
    },
  ],
  DK: [
    {
      type: 'CPR',
      regex: '^(\\d{6}-\\d{4}|\\d{10})$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456-7890',
    },
  ],
  EE: [
    {
      type: 'ISIKUKOOD',
      regex: '^[1-8]\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],
  ES: [
    {
      type: 'DNI',
      regex: '^\\d{8}[A-Z]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678Z',
      validatorKey: 'isDNI',
    },
    {
      type: 'NIE',
      regex: '^[XYZ]\\d{7}[A-Z]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'X1234567L',
      validatorKey: 'isNIE',
    },
    {
      type: 'CIF',
      regex: '^[A-HJ-NP-SUVW]\\d{7}[0-9A-J]$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: 'B12345674',
      validatorKey: 'isCIF',
    },
  ],
  FI: [
    {
      type: 'HETU',
      regex: '^\\d{6}[-+ABCDEFYXWVU]\\d{3}[0-9A-Y]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456-789A',
    },
  ],
  FR: [
    {
      // CNIe (desde 2021) 9 alfanumericos; CNI antigua 12 (el codigo de oficina puede llevar letras)
      type: 'CNI',
      regex: '^(?:[A-Z0-9]{9}|[A-Z0-9]{12})$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB1234567',
    },
    {
      type: 'NUMÉRO_FISCAL',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
    {
      type: 'SIREN',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789',
    },
  ],
  GR: [
    {
      type: 'ADT',
      regex: '^[A-ZΑ-Ω]{1,2} ?\\d{6,7}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB123456',
    },
    {
      type: 'AFM',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789',
    },
  ],
  HR: [
    {
      type: 'OIB',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],
  IT: [
    {
      type: 'CIE',
      regex: '^[A-Z]{2}\\d{5}[A-Z]{2}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB12345CD',
    },
    {
      type: 'CODICE_FISCALE',
      regex: '^[A-Z]{6}\\d{2}[A-Z]\\d{2}[A-Z]\\d{3}[A-Z]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'ABCDEF12A34B567C',
    },
    {
      type: 'P.IVA',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '12345678901',
    },
  ],
  LT: [
    {
      type: 'ASMENS_KODAS',
      regex: '^[1-6]\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],
  LU: [
    {
      type: 'MATRICULE',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
    {
      type: 'MATRICULE',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '12345678901',
    },
  ],
  LV: [
    {
      type: 'PERSONAS_KODS',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
    {
      type: 'PVN',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '12345678901',
    },
  ],
  MT: [
    {
      type: 'PERSONAL_ID',
      regex: '^\\d{1,7}[ABGHMLPZ]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567A',
    },
    {
      type: 'TIN',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789',
    },
  ],
  NL: [
    {
      type: 'BSN',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789',
    },
    {
      type: 'PASSPORT',
      regex: '^(?=.*[A-Z])[A-Z0-9]{6,9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB1234567',
    },
  ],
  NO: [
    {
      type: 'FØDSELSNUMMER',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
    {
      type: 'Org.nr',
      regex: '^[89]\\d{8}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '912345678',
    },
  ],
  PL: [
    {
      type: 'PESEL',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
    {
      type: 'DOWÓD_OSOBISTY',
      regex: '^[A-Z]{3}\\d{6}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'ABC123456',
    },
  ],
  PT: [
    {
      type: 'CC',
      regex: '^\\d{9}[A-Z0-9]{2}\\d$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789AB0',
    },
    {
      type: 'NIF',
      regex: '^[1-4]\\d{7}\\d$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789',
    },
  ],
  RO: [
    {
      type: 'CNP',
      regex: '^[1-8]\\d{12}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
    {
      type: 'NIF',
      regex: '^9\\d{12}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '9123456789012',
    },
  ],
  RU: [
    {
      type: 'INN',
      regex: '^\\d{10}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '1234567890',
    },
  ],
  SE: [
    {
      type: 'PERSONNUMMER',
      regex: '^(?:\\d{6}[-+]\\d{4}|\\d{10}|\\d{12})$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890',
    },
  ],
  SI: [
    {
      type: 'EMŠO',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
    {
      type: 'OSEBNA_IZKAZNICA',
      regex: '^[A-Z0-9]{8,9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB1234567',
    },
    {
      type: 'DAVČNA_ŠTEVILKA',
      regex: '^[1-9]\\d{6}[0-9]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678',
    },
  ],
  SK: [
    {
      type: 'DIČ',
      regex: '^\\d{10}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '1234567890',
    },
  ],
  TR: [
    {
      type: 'TCKN',
      regex: '^[1-9]\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],
  XK: [
    {
      type: 'NIN',
      regex: '^\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890',
    },
    {
      type: 'NUI',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789',
    },
  ],
  AR: [
    {
      type: 'DNI',
      regex: '^\\d{6,8}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678',
    },
    {
      type: 'CUIT',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],
  BR: [
    {
      type: 'CPF',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901',
    },
  ],
  CA: [
    {
      type: 'SIN',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789',
    },
    { type: 'DRIVER_LICENSE', regex: '^[A-Z0-9]{5,20}$', scope: DOCUMENT_SCOPE.PARTICULAR, example: 'AB1234567' },
  ],
  CL: [
    {
      type: 'RUT',
      regex: '^(?:\\d{7,8}|\\d{1,2}\\.?\\d{3}\\.?\\d{3})-?[\\dkK]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12.345.678-9',
    },
  ],
  CO: [
    {
      type: 'CC',
      regex: '^[A-Z0-9-]{5,20}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB123456',
    },
    {
      type: 'NIT',
      regex: '^\\d{10,11}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890',
    },
  ],
  DO: [
    {
      type: 'RNC',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789',
    },
  ],
  EC: [
    {
      type: 'CI',
      regex: '^\\d{10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890',
    },
  ],
  GT: [
    {
      type: 'DPI',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
  ],
  MX: [
    {
      type: 'CURP',
      regex:
        '^[A-Z][AEIOUX][A-Z]{2}\\d{6}[HMX](?:AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\\d$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AAAA123456HDFBBB01',
    },
    {
      type: 'RFC',
      regex: '^[A-Z&Ñ]{3}\\d{6}[A-Z0-9]{3}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: 'ABC123456AB1',
    },
  ],
  US: [
    {
      type: 'DRIVER_LICENSE',
      regex: '^[A-Z0-9]{5,20}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'A1234567',
    },
    {
      type: 'STATE_ID',
      regex: '^[A-Z0-9]{5,20}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB1234567',
    },
    {
      type: 'EIN',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789',
    },
  ],
  VE: [
    {
      type: 'CEDULA',
      regex: '^[VE]\\d{6,9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'V12345678',
    },
    {
      type: 'RIF',
      regex: '^[JG]\\d{9}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: 'J123456789',
    },
  ],
  CN: [
    {
      type: 'RIC',
      regex: '^\\d{17}[\\dXx]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '12345678901234567X',
    },
    {
      type: 'USCC',
      regex: '^[0-9A-HJ-NPQRTUWXY]{18}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789012345678',
    },
  ],
  HK: [
    {
      type: 'HKID',
      regex: '^[A-Z]{1,2}\\d{6}\\([0-9A]\\)$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'A123456(7)',
    },
    {
      type: 'BRN',
      regex: '^\\d{8}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '12345678',
    },
  ],
  IL: [
    {
      type: 'TEUDAT_ZEHUT',
      regex: '^\\d{9}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789',
    },
  ],
  IN: [
    {
      type: 'GSTIN',
      regex: '^\\d{2}[A-Z]{5}\\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '12ABCDE1234F1Z5',
    },
  ],
  JP: [
    {
      type: 'MY_NUMBER',
      regex: '^\\d{12}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789012',
    },
    {
      type: 'Hōjin',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '1234567890123',
    },
  ],
  KR: [
    {
      type: 'RRN',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
  ],
  MY: [
    {
      type: 'NRIC',
      regex: '^\\d{12}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789012',
    },
  ],

  SG: [
    {
      type: 'NRIC',
      regex: '^[STFGM]\\d{7}[A-Z]$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'S1234567A',
    },
    {
      type: 'UEN',
      regex: '^(?:\\d{8}[A-Z]|\\d{9}[A-Z]|[RST]\\d{2}[A-Z]{2}\\d{4}[A-Z])$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '123456789A',
    },
  ],
  TH: [
    {
      type: 'NATIONAL_ID',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '1234567890123',
    },
    {
      type: 'TIN',
      regex: '^\\d{13}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '1234567890123',
    },
  ],
  VN: [
    {
      type: 'CCCD',
      regex: '^\\d{12}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: '123456789012',
    },
    {
      type: 'MST',
      regex: '^\\d{10}(?:\\d{3})?$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '1234567890',
    },
  ],
  AU: [
    {
      type: 'DRIVER_LICENSE',
      regex: '^[A-Z0-9]{5,12}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB1234567',
    },
    {
      type: 'ABN',
      regex: '^\\d{11}$',
      scope: DOCUMENT_SCOPE.EMPRESA,
      example: '12345678901',
    },
  ],
  NZ: [
    {
      type: 'DRIVER_LICENSE',
      regex: '^[A-Z0-9]{5,10}$',
      scope: DOCUMENT_SCOPE.PARTICULAR,
      example: 'AB1234567',
    },
  ],
};

/** AB123456 = ejemplo de documento generico (5-20 letras/numeros), no un NIF concreto de un pais. */
const GENERIC_DOCUMENT_EXAMPLE = 'AB123456';
const PASSPORT_EXAMPLE = 'AB123456';

const GENERIC_ID = {
  type: 'ID',
  regex: '^(?=.*\\d)[A-Z0-9][A-Z0-9 ./-]{2,28}[A-Z0-9]$',
  scope: DOCUMENT_SCOPE.PARTICULAR,
  example: GENERIC_DOCUMENT_EXAMPLE,
};

const PASSPORT = {
  type: 'PASSPORT',
  regex: '^[A-Z0-9]{6,9}$',
  scope: DOCUMENT_SCOPE.PASSPORT,
  example: PASSPORT_EXAMPLE,
};

const DOCUMENT_PHRASE_REFUSAL =
  /^(NO(TENGO|TIENE|DISPONGO|HAY)|SIN(DNI|PASAPORTE|DOCUMENTO|DOC|ID)|PENDIENTE|DESCONOCIDO|UNKNOWN|N\/A|NODNI|NOPASAPORTE)|NOTENGO|NOTIENE|DONTHAVE/;

export function formatDocumentoInput(event) {
  event.target.value = event.target.value.trim().toUpperCase();
  return event.target.value;
}

function isFreeTextDocument(value) {
  if (value.length < 5) return false;
  if (DOCUMENT_PHRASE_REFUSAL.test(value)) return true;
  return !/\d/.test(value) && /^[A-Z]+$/.test(value);
}

function applicableRulesForCountry(countryIso, documentScope) {
  if (documentScope === DOCUMENT_SCOPE.PASSPORT) return [PASSPORT];
  const countryRules = DOCUMENT_RULES_BY_COUNTRY[countryIso] ?? [];
  const scopedRules = countryRules.filter(rule => rule.scope === documentScope);
  // Sin reglas para el scope: ID generico, nunca reglas de otro scope.
  return scopedRules.length ? scopedRules : [GENERIC_ID];
}

export function checkDocumento(isoCode, value, { isEntity = false, passport = false } = {}) {
  const countryIso = String(isoCode || '')
    .trim()
    .toUpperCase();
  const documentValue = String(value ?? '');
  const documentScope = passport
    ? DOCUMENT_SCOPE.PASSPORT
    : isEntity
    ? DOCUMENT_SCOPE.EMPRESA
    : DOCUMENT_SCOPE.PARTICULAR;

  const applicableRules = applicableRulesForCountry(countryIso, documentScope);

  const hints = [...new Set(applicableRules.map(rule => `${rule.type} ${rule.example}`))].join(', ');
  const example = applicableRules[0].example;
  const validationResult = isValid => ({ isValid, hints, example });

  if (!documentValue) return validationResult(false);
  if (isFreeTextDocument(documentValue)) return validationResult(false);
  if (!countryIso) return validationResult(true);

  for (const rule of applicableRules) {
    if (!new RegExp(rule.regex).test(documentValue)) continue;
    if (rule.validatorKey && !validateWithValidator(rule.validatorKey, documentValue)) continue;
    return validationResult(true);
  }
  return validationResult(false);
}
